import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '../AppContext'
import { Actions } from '../AppReducer'
import { showNotification } from '../util/notifications'
import SearchTrackControls from './SearchTrackControls'
import { useQuery } from '@apollo/client/react'
import DigQuery from '../graphql/queries/Dig'
import useCurrentUser from '../hooks/useCurrentUser'
import useCurrentRoom from '../hooks/useCurrentRoom'
import { Box, Group, Stack, Text, Table, Loader, Button } from '@mantine/core'
import '@mantine/core/styles.css'
import TagStringEditor from '../ui/TagStringEditor'

export default function Dig() {

    const dispatch = useAppDispatch()
    const user = useCurrentUser()
    const currentRoom = useCurrentRoom()

    const blankParams = useMemo(() => {
        return {
            tags: currentRoom?.spinning ?? '',
            roomId: currentRoom?.id
        }
    }, [currentRoom?.spinning, currentRoom?.id])

    const [params, setParams] = useState(blankParams)
    const [shouldFetch, setShouldFetch] = useState(false)
    const [hasAutoFetched, setHasAutoFetched] = useState(false)

    useEffect(() => {
        setParams(blankParams)
        setShouldFetch(false)
        setHasAutoFetched(false)
    }, [user?.id, blankParams])

    // Auto-fetch on mount if we have a roomId
    useEffect(() => {
        if (!hasAutoFetched && params.roomId) {
            setHasAutoFetched(true)
            setShouldFetch(true)
        }
    }, [hasAutoFetched, params.roomId])

    const { data, loading: tracksLoading, refetch } = useQuery(DigQuery, {
        skip: !shouldFetch || !params.roomId,
        fetchPolicy: 'network-only',
        variables: {
            roomId: params.roomId,
            tags: params.tags,
            scored: true,
            limit: 10
        }
    })

    const trackList = data?.dig ?? []

    async function handleDig() {
        if (!params.roomId) {
            showNotification({ content: 'No room selected.' })
            return
        }

        setShouldFetch(true)

        try {
            await refetch({
                roomId: params.roomId,
                tags: params.tags,
                scored: false,
                limit: 10
            })
        } catch (error) {
            console.error('Dig error:', error)
            showNotification({ content: 'Failed to dig for tracks.' })
        }
    }

    function paramsUpdated(field, value) {
        console.log(`paramsUpdated => ${field}: ${value}`)
        const newParams = { ...params }
        newParams[field] = value
        setParams(newParams)
    }

    let noResultsContent
    if (!tracksLoading && trackList.length === 0)
        noResultsContent =
            <Box p="xl" ta="center">
                <Text c="dimmed" size="lg" mb="md">No tracks found.</Text>
                <Text c="dimmed" size="sm">
                    Try different tags.
                </Text>
            </Box>

    return (
        <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Dig Parameters */}
            <Box
                p="sm"
                style={{
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(0,0,0,0.2)'
                }}
            >
                <Stack gap="xs">
                    <Group gap="xs" wrap="nowrap">
                        <span className="material-symbols-outlined" style={{ color: 'var(--mantine-color-dimmed)' }}>tag</span>
                        <TagStringEditor
                            roomId={params.roomId}
                            placeholder="Enter tags to dig for..."
                            value={params.tags}
                            onChange={(value) => paramsUpdated('tags', value)}
                            style={{ flex: 1 }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleDig()
                                }
                            }}
                        />
                        <Button
                            onClick={handleDig}
                            loading={tracksLoading}
                            leftSection={<span className="material-symbols-outlined">shuffle</span>}
                        >
                            Dig
                        </Button>
                    </Group>
                    <Text size="xs" c="dimmed">
                        Use tag operators like "rock or pop", "jazz not fusion", etc.
                    </Text>
                </Stack>
            </Box>

            {/* Dig Results */}
            <Box
                style={{
                    flex: 1,
                    overflowY: 'auto',
                    position: 'relative'
                }}
            >
                <Box style={{
                    opacity: tracksLoading ? 0.4 : 1,
                    transition: 'opacity 200ms ease-in-out',
                    pointerEvents: tracksLoading ? 'none' : 'auto'
                }}>
                    {trackList?.length > 0 ? (
                        <Table highlightOnHover>
                            <Table.Thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--mantine-color-dark-7)', zIndex: 1 }}>
                                <Table.Tr>
                                    <Table.Th style={{ width: '120px', minWidth: '120px', textAlign: 'center' }}></Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Artist</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Title</Table.Th>
                                    <Table.Th style={{ textAlign: 'center' }}>Album</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {trackList.map((track, index) => (
                                    <Table.Tr key={`${track.id}-${index}`}>
                                        <Table.Td style={{ width: '120px', minWidth: '120px', textAlign: 'center' }}>
                                            <SearchTrackControls track={track} isFirstRow={index === 0} />
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'center' }}>
                                            <Text size="sm">{track.artist}</Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'center' }}>
                                            <Text
                                                size="sm"
                                                c="blue"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => dispatch(Actions.showTrackModal(track.id))}
                                            >
                                                {track.title ?? '[ untitled ]'}
                                            </Text>
                                        </Table.Td>
                                        <Table.Td style={{ textAlign: 'center' }}>
                                            <Text
                                                size="sm"
                                                c="blue"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => dispatch(Actions.showAlbumModal(track))}
                                            >
                                                {track.album}
                                            </Text>
                                        </Table.Td>
                                    </Table.Tr>
                                ))}
                            </Table.Tbody>
                        </Table>
                    ) : (
                        noResultsContent
                    )}
                </Box>
                {tracksLoading && (
                    <Box
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 2
                        }}
                    >
                        <Loader size="lg" />
                    </Box>
                )}
            </Box>
        </Box>
    )
}

